import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const THEME_KEY = "dams_theme";
const ThemeContext = createContext(null);

const detectInitialTheme = () => {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === "light" || saved === "dark") return saved;
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
  } catch (_) {
    // localStorage may throw in private/locked browsers — fall back to light.
  }
  return "light";
};

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(detectInitialTheme);

  // Apply the class on <html> and persist. Cleanup on unmount removes the
  // class so the admin panel (which doesn't mount this provider) renders light.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle("dark", theme === "dark");
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch (_) {
      /* ignore */
    }
    return () => {
      root.classList.remove("dark");
    };
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === "dark" ? "dark" : "light");
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider");
  return ctx;
};
